import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, BookOpen, Award, Plus, X, Edit2, Trash2, ArrowUp, ArrowDown, Eye, Tag, Loader2, GripVertical } from 'lucide-react';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import CouponCodeModal from '../../components/CouponCodeModal';
import { supabase } from '../../supabase/client';

const orgBgColors = [
  'bg-amber-50', 'bg-emerald-50', 'bg-sky-50', 'bg-rose-50', 'bg-violet-50',
  'bg-teal-50', 'bg-orange-50', 'bg-indigo-50', 'bg-pink-50', 'bg-cyan-50',
];

function getOrgColor(orgId: string, organizations: Organization[]): string {
  const index = organizations.findIndex(o => o.id === orgId);
  return orgBgColors[index % orgBgColors.length];
}

function formatPrice(yuan: number): string {
  if (yuan === 0) return '免费';
  return `¥${yuan.toFixed(1)}`;
}

interface OrgModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  initialValue?: string;
  onSubmit: (name: string) => void;
}

function OrgModal({ isOpen, onClose, title, initialValue = '', onSubmit }: OrgModalProps) {
  const [name, setName] = useState(initialValue);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => { if (isOpen) setName(initialValue); }, [isOpen, initialValue]);
  const handleSubmit = () => { if (name.trim()) { onSubmit(name.trim()); setName(''); } };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
          <motion.div
            drag
            dragConstraints={{ left: -400, right: 400, top: -300, bottom: 300 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_, info) => {
              setIsDragging(false);
              setPosition(prev => ({ x: prev.x + info.offset.x, y: prev.y + info.offset.y }));
            }}
            initial={{ opacity: 0, scale: 0.95, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x: position.x, y: position.y }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-96 overflow-hidden ${isDragging ? 'ring-2 ring-blue-400/50 shadow-2xl' : ''}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white ${isDragging ? 'bg-blue-50/50' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <h3 className="text-lg font-semibold select-none">{title}</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">名称</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="请输入名称" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">取消</button>
                <button onClick={handleSubmit} disabled={!name.trim()} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300">保存</button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface CatModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  organizations: Organization[];
  initialValue?: { name: string; orgId: string };
  onSubmit: (name: string, orgId: string) => void;
}

function CatModal({ isOpen, onClose, title, organizations, initialValue, onSubmit }: CatModalProps) {
  const [name, setName] = useState(initialValue?.name || '');
  const [orgId, setOrgId] = useState(initialValue?.orgId || '');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialValue?.name || '');
      setOrgId(initialValue?.orgId || organizations[0]?.id || '');
    }
  }, [isOpen, initialValue, organizations]);
  const handleSubmit = () => { if (name.trim() && orgId) { onSubmit(name.trim(), orgId); setName(''); setOrgId(''); } };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
          <motion.div
            drag
            dragConstraints={{ left: -400, right: 400, top: -300, bottom: 300 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_, info) => {
              setIsDragging(false);
              setPosition(prev => ({ x: prev.x + info.offset.x, y: prev.y + info.offset.y }));
            }}
            initial={{ opacity: 0, scale: 0.95, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x: position.x, y: position.y }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-96 overflow-hidden ${isDragging ? 'ring-2 ring-blue-400/50 shadow-2xl' : ''}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white ${isDragging ? 'bg-blue-50/50' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <h3 className="text-lg font-semibold select-none">{title}</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">所属考评体系</label>
                <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                  {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">名称</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="请输入名称" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">取消</button>
                <button onClick={handleSubmit} disabled={!name.trim() || !orgId} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300">保存</button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface LevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  organizations: Organization[];
  categories: CourseCategory[];
  initialValue?: { name: string; orgId: string; catId: string; priceMonth?: number; priceQuarter?: number; priceHalfYear?: number; priceYear?: number };
  onSubmit: (name: string, catId: string, prices: { priceMonth: number; priceQuarter: number; priceHalfYear: number; priceYear: number }) => void;
}

function LevelModal({ isOpen, onClose, title, organizations, categories, initialValue, onSubmit }: LevelModalProps) {
  const [name, setName] = useState(initialValue?.name || '');
  const [orgId, setOrgId] = useState(initialValue?.orgId || '');
  const [catId, setCatId] = useState(initialValue?.catId || '');
  const [priceMonth, setPriceMonth] = useState((initialValue?.priceMonth || 0).toFixed(1));
  const [priceQuarter, setPriceQuarter] = useState((initialValue?.priceQuarter || 0).toFixed(1));
  const [priceHalfYear, setPriceHalfYear] = useState((initialValue?.priceHalfYear || 0).toFixed(1));
  const [priceYear, setPriceYear] = useState((initialValue?.priceYear || 0).toFixed(1));
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const filteredCats = orgId ? categories.filter(c => c.orgId === orgId) : categories;

  useEffect(() => {
    if (isOpen) {
      setName(initialValue?.name || '');
      setOrgId(initialValue?.orgId || organizations[0]?.id || '');
      setCatId(initialValue?.catId || '');
      setPriceMonth((initialValue?.priceMonth || 0).toFixed(1));
      setPriceQuarter((initialValue?.priceQuarter || 0).toFixed(1));
      setPriceHalfYear((initialValue?.priceHalfYear || 0).toFixed(1));
      setPriceYear((initialValue?.priceYear || 0).toFixed(1));
    }
  }, [isOpen, initialValue, organizations]);

  useEffect(() => {
    if (orgId && filteredCats.length > 0 && !filteredCats.find(c => c.id === catId)) {
      setCatId(filteredCats[0]?.id || '');
    }
  }, [orgId, categories]);

  const handleSubmit = () => {
    if (name.trim() && catId) {
      const prices = {
        priceMonth: Math.max(0, parseFloat(priceMonth) || 0),
        priceQuarter: Math.max(0, parseFloat(priceQuarter) || 0),
        priceHalfYear: Math.max(0, parseFloat(priceHalfYear) || 0),
        priceYear: Math.max(0, parseFloat(priceYear) || 0),
      };
      onSubmit(name.trim(), catId, prices);
      setName(''); setOrgId(''); setCatId('');
      setPriceMonth('0.0'); setPriceQuarter('0.0'); setPriceHalfYear('0.0'); setPriceYear('0.0');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
          <motion.div
            drag
            dragConstraints={{ left: -400, right: 400, top: -300, bottom: 300 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_, info) => {
              setIsDragging(false);
              setPosition(prev => ({ x: prev.x + info.offset.x, y: prev.y + info.offset.y }));
            }}
            initial={{ opacity: 0, scale: 0.95, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x: position.x, y: position.y }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-[480px] overflow-hidden ${isDragging ? 'ring-2 ring-blue-400/50 shadow-2xl' : ''}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white ${isDragging ? 'bg-blue-50/50' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <h3 className="text-lg font-semibold select-none">{title}</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">所属考评体系</label>
              <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setCatId(''); }} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
                {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">所属课程分类</label>
              <select value={catId} onChange={(e) => setCatId(e.target.value)} disabled={!orgId || filteredCats.length === 0} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100">
                <option value="">请选择</option>
                {filteredCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">名称</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="请输入名称" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">刷题包价格</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">月包（元）</label>
                  <input type="number" min="0" step="0.1" value={priceMonth} onChange={(e) => setPriceMonth(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">季包（元）</label>
                  <input type="number" min="0" step="0.1" value={priceQuarter} onChange={(e) => setPriceQuarter(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">半年包（元）</label>
                  <input type="number" min="0" step="0.1" value={priceHalfYear} onChange={(e) => setPriceHalfYear(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">年包（元）</label>
                  <input type="number" min="0" step="0.1" value={priceYear} onChange={(e) => setPriceYear(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">取消</button>
              <button onClick={handleSubmit} disabled={!name.trim() || !catId} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300">保存</button>
            </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface BatchPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  onSubmit: (prices: { priceMonth: number; priceQuarter: number; priceHalfYear: number; priceYear: number }) => void;
}

function BatchPriceModal({ isOpen, onClose, selectedCount, onSubmit }: BatchPriceModalProps) {
  const [priceMonth, setPriceMonth] = useState('0');
  const [priceQuarter, setPriceQuarter] = useState('0');
  const [priceHalfYear, setPriceHalfYear] = useState('0');
  const [priceYear, setPriceYear] = useState('0');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPriceMonth('0');
      setPriceQuarter('0');
      setPriceHalfYear('0');
      setPriceYear('0');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({
      priceMonth: Math.max(0, parseFloat(priceMonth) || 0),
      priceQuarter: Math.max(0, parseFloat(priceQuarter) || 0),
      priceHalfYear: Math.max(0, parseFloat(priceHalfYear) || 0),
      priceYear: Math.max(0, parseFloat(priceYear) || 0),
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
          <motion.div
            drag
            dragConstraints={{ left: -400, right: 400, top: -300, bottom: 300 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_, info) => {
              setIsDragging(false);
              setPosition(prev => ({ x: prev.x + info.offset.x, y: prev.y + info.offset.y }));
            }}
            initial={{ opacity: 0, scale: 0.95, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x: position.x, y: position.y }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-[400px] overflow-hidden ${isDragging ? 'ring-2 ring-blue-400/50 shadow-2xl' : ''}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white ${isDragging ? 'bg-blue-50/50' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <h3 className="text-lg font-semibold select-none">批量定价</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">已选择 {selectedCount} 个级别，统一设置价格：</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div>
                <label className="block text-xs text-gray-500 mb-1">月包（元）</label>
                <input type="number" min="0" step="0.1" value={priceMonth} onChange={(e) => setPriceMonth(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">季包（元）</label>
                <input type="number" min="0" step="0.1" value={priceQuarter} onChange={(e) => setPriceQuarter(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">半年包（元）</label>
                <input type="number" min="0" step="0.1" value={priceHalfYear} onChange={(e) => setPriceHalfYear(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">年包（元）</label>
                <input type="number" min="0" step="0.1" value={priceYear} onChange={(e) => setPriceYear(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">取消</button>
              <button onClick={handleSubmit} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">确认设置</button>
            </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface ConfirmModalProps { isOpen: boolean; onClose: () => void; title: string; message: string; onConfirm: () => void; }
function ConfirmModal({ isOpen, onClose, title, message, onConfirm }: ConfirmModalProps) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
          <motion.div
            drag
            dragConstraints={{ left: -400, right: 400, top: -300, bottom: 300 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_, info) => {
              setIsDragging(false);
              setPosition(prev => ({ x: prev.x + info.offset.x, y: prev.y + info.offset.y }));
            }}
            initial={{ opacity: 0, scale: 0.95, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x: position.x, y: position.y }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-96 overflow-hidden ${isDragging ? 'ring-2 ring-blue-400/50 shadow-2xl' : ''}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white ${isDragging ? 'bg-blue-50/50' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <h3 className="text-lg font-semibold select-none">{title}</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">取消</button>
                <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">确认删除</button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface EditablePriceCellProps {
  value: number;
  onSave: (value: number) => void;
}

function EditablePriceCell({ value, onSave }: EditablePriceCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toFixed(1));

  const handleSave = () => {
    const numValue = Math.max(0, parseFloat(editValue) || 0);
    onSave(numValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value.toFixed(1));
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type="number"
        min="0"
        step="0.1"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-20 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 outline-none"
        autoFocus
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:text-blue-600 hover:underline px-2 py-1 rounded hover:bg-blue-50 transition-colors"
    >
      {formatPrice(value)}
    </span>
  );
}

interface EditableCountCellProps {
  value: number;
  onSave: (value: number) => void;
}

function EditableCountCell({ value, onSave }: EditableCountCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  const handleSave = () => {
    const numValue = Math.max(0, parseInt(editValue) || 0);
    onSave(numValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type="number"
        min="0"
        step="1"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-14 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 outline-none text-center"
        autoFocus
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:text-blue-600 hover:underline px-2 py-1 rounded hover:bg-blue-50 transition-colors"
    >
      {value}
    </span>
  );
}

export default function CategoryManager() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'org' | 'course' | 'level'>('org');
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [levelModalOpen, setLevelModalOpen] = useState(false);
  const [batchPriceModalOpen, setBatchPriceModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editingCat, setEditingCat] = useState<CourseCategory | null>(null);
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);
  const [deletingCat, setDeletingCat] = useState<CourseCategory | null>(null);
  const [deletingLevel, setDeletingLevel] = useState<Level | null>(null);
  const [batchDeletingOrgs, setBatchDeletingOrgs] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [selectedOrganizations, setSelectedOrganizations] = useState<Set<string>>(new Set());
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [selectedLevelForCoupon, setSelectedLevelForCoupon] = useState<Level | null>(null);

  const syncDataFromStore = useCallback(() => {
    setOrganizations(categoryStore.getOrganizations());
    setCategories(categoryStore.getCategories());
    setLevels(categoryStore.getLevels());
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initData = async () => {
      setIsLoading(true);
      try {
        await categoryStore.forceReload();
        if (isMounted) {
          syncDataFromStore();
        }
      } catch (error) {
        
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initData();

    const unsubscribe = categoryStore.subscribe(() => {
      if (isMounted) {
        syncDataFromStore();
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [syncDataFromStore]);

  const getOrgName = (id: string) => organizations.find(o => o.id === id)?.name || '-';
  const getCatName = (id: string) => categories.find(c => c.id === id)?.name || '-';

  const handleAddOrganization = async (name: string) => {
    try {
      await categoryStore.addOrganization({ name });
      setOrgModalOpen(false);
    } catch (error: any) {
      alert(error.message || '添加失败');
    }
  };

  const handleEditOrganization = async (name: string) => {
    if (editingOrg) {
      try {
        await categoryStore.updateOrganization(editingOrg.id, { name });
        setEditingOrg(null);
      } catch (error: any) {
        alert(error.message || '更新失败');
      }
    }
  };

  const handleDeleteOrganization = async () => {
    if (deletingOrg) {
      try {
        await categoryStore.deleteOrganization(deletingOrg.id);
        setDeletingOrg(null);
      } catch (error: any) {
        alert(error.message || '删除失败');
      }
    }
  };

  const handleMoveOrganization = async (id: string, direction: 'up' | 'down') => {
    try {
      await categoryStore.moveOrganization(id, direction);
    } catch (error: any) {
      alert(error.message || '移动失败');
    }
  };

  const handleAddCategory = async (name: string, orgId: string) => {
    try {
      await categoryStore.addCategory({ name, code: name.toLowerCase(), orgId });
      setCatModalOpen(false);
    } catch (error: any) {
      alert(error.message || '添加失败');
    }
  };

  const handleEditCategory = async (name: string, orgId: string) => {
    if (editingCat) {
      try {
        await categoryStore.updateCategory(editingCat.id, { name, orgId });
        setEditingCat(null);
      } catch (error: any) {
        alert(error.message || '更新失败');
      }
    }
  };

  const handleDeleteCategory = async () => {
    if (deletingCat) {
      try {
        await categoryStore.deleteCategory(deletingCat.id);
        setDeletingCat(null);
      } catch (error: any) {
        alert(error.message || '删除失败');
      }
    }
  };

  const handleMoveCategory = async (id: string, direction: 'up' | 'down') => {
    try {
      await categoryStore.moveCategory(id, direction);
    } catch (error: any) {
      alert(error.message || '移动失败');
    }
  };

  const handleAddLevel = async (name: string, catId: string, prices: { priceMonth: number; priceQuarter: number; priceHalfYear: number; priceYear: number }) => {
    try {
      await categoryStore.addLevel({
        name,
        catId,
        ...prices,
        singleChoiceCount: 0,
        multiChoiceCount: 0,
        judgeCount: 0,
        programmingCount: 0
      });
      setLevelModalOpen(false);
    } catch (error: any) {
      alert(error.message || '添加失败');
    }
  };

  const handleEditLevel = async (name: string, catId: string, prices: { priceMonth: number; priceQuarter: number; priceHalfYear: number; priceYear: number }) => {
    if (editingLevel) {
      try {
        await categoryStore.updateLevel(editingLevel.id, {
          name,
          catId,
          ...prices,
          singleChoiceCount: editingLevel.singleChoiceCount,
          multiChoiceCount: editingLevel.multiChoiceCount,
          judgeCount: editingLevel.judgeCount,
          programmingCount: editingLevel.programmingCount
        });
        setEditingLevel(null);
      } catch (error: any) {
        alert(error.message || '更新失败');
      }
    }
  };

  const handleUpdateLevelPrice = async (levelId: string, priceType: 'priceMonth' | 'priceQuarter' | 'priceHalfYear' | 'priceYear', value: number) => {
    try {
      const typeMap: Record<string, 'month' | 'quarter' | 'halfYear' | 'year'> = {
        priceMonth: 'month',
        priceQuarter: 'quarter',
        priceHalfYear: 'halfYear',
        priceYear: 'year'
      };
      await categoryStore.updateLevelPrice(levelId, typeMap[priceType], value);
    } catch (error: any) {
      alert(error.message || '更新价格失败');
    }
  };

  const handleUpdateLevelQuestionConfig = async (levelId: string, configType: 'singleChoiceCount' | 'multiChoiceCount' | 'judgeCount' | 'programmingCount', value: number) => {
    try {
      const columnMap: Record<string, string> = {
        singleChoiceCount: 'single_choice_count',
        multiChoiceCount: 'multi_choice_count',
        judgeCount: 'judge_count',
        programmingCount: 'programming_count'
      };
      const { error } = await supabase
        .from('exam_levels')
        .update({ [columnMap[configType]]: value })
        .eq('id', parseInt(levelId));
      if (error) throw error;
      await categoryStore.reloadAll();
    } catch (error: any) {
      alert(error.message || '更新题型配置失败');
    }
  };

  const handleBatchUpdatePrices = async (prices: { priceMonth: number; priceQuarter: number; priceHalfYear: number; priceYear: number }) => {
    try {
      await categoryStore.batchUpdateLevelPrices(Array.from(selectedLevels), {
        month: prices.priceMonth,
        quarter: prices.priceQuarter,
        halfYear: prices.priceHalfYear,
        year: prices.priceYear
      });
      setBatchPriceModalOpen(false);
      setSelectedLevels(new Set());
    } catch (error: any) {
      alert(error.message || '批量更新失败');
    }
  };

  const handleDeleteLevel = async () => {
    if (deletingLevel) {
      try {
        await categoryStore.deleteLevel(deletingLevel.id);
        setDeletingLevel(null);
      } catch (error: any) {
        alert(error.message || '删除失败');
      }
    }
  };

  const handleMoveLevel = async (id: string, direction: 'up' | 'down') => {
    try {
      await categoryStore.moveLevel(id, direction);
    } catch (error: any) {
      alert(error.message || '移动失败');
    }
  };

  const toggleSelectLevel = (id: string) => {
    const newSet = new Set(selectedLevels);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedLevels(newSet);
  };

  const toggleSelectAllLevels = () => {
    if (selectedLevels.size === levels.length && levels.length > 0) setSelectedLevels(new Set());
    else setSelectedLevels(new Set(levels.map(l => l.id)));
  };

  const handleBatchDeleteLevels = async () => {
    if (selectedLevels.size === 0) return;
    try {
      const ids = Array.from(selectedLevels);
      for (const id of ids) {
        await categoryStore.deleteLevel(id);
      }
      setSelectedLevels(new Set());
    } catch (error: any) {
      alert(error.message || '批量删除失败');
    }
  };

  const toggleSelectCategory = (id: string) => {
    const newSet = new Set(selectedCategories);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedCategories(newSet);
  };

  const toggleSelectAllCategories = () => {
    if (selectedCategories.size === categories.length && categories.length > 0) setSelectedCategories(new Set());
    else setSelectedCategories(new Set(categories.map(c => c.id)));
  };

  const handleBatchDeleteCategories = async () => {
    if (selectedCategories.size === 0) return;
    try {
      const ids = Array.from(selectedCategories);
      for (const id of ids) {
        await categoryStore.deleteCategory(id);
      }
      setSelectedCategories(new Set());
    } catch (error: any) {
      alert(error.message || '批量删除失败');
    }
  };

  const toggleSelectOrganization = (id: string) => {
    const newSet = new Set(selectedOrganizations);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedOrganizations(newSet);
  };

  const toggleSelectAllOrganizations = () => {
    if (selectedOrganizations.size === organizations.length && organizations.length > 0) {
      setSelectedOrganizations(new Set());
    } else {
      setSelectedOrganizations(new Set(organizations.map(o => o.id)));
    }
  };

  const handleBatchDeleteOrganizations = async () => {
    if (selectedOrganizations.size === 0) return;
    setBatchDeletingOrgs(true);
  };

  const confirmBatchDeleteOrganizations = async () => {
    try {
      const ids = Array.from(selectedOrganizations);
      for (const id of ids) {
        await categoryStore.deleteOrganization(id);
      }
      setSelectedOrganizations(new Set());
      setBatchDeletingOrgs(false);
    } catch (error: any) {
      alert(error.message || '批量删除失败');
    }
  };

  const tabs = [
    { key: 'org', label: '考评体系', icon: Building2 },
    { key: 'course', label: '课程分类', icon: BookOpen },
    { key: 'level', label: '级别分类', icon: Award },
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-2">类目与定价</h2>
      <p className="text-gray-500 mb-6">管理考评体系、课程分类、级别分类类目数据</p>
      <OrgModal isOpen={orgModalOpen} onClose={() => setOrgModalOpen(false)} title="新增考评体系" onSubmit={handleAddOrganization} />
      <OrgModal isOpen={!!editingOrg} onClose={() => setEditingOrg(null)} title="编辑考评体系" initialValue={editingOrg?.name} onSubmit={handleEditOrganization} />
      <CatModal isOpen={catModalOpen} onClose={() => setCatModalOpen(false)} title="新增课程分类" organizations={organizations} onSubmit={handleAddCategory} />
      <CatModal isOpen={!!editingCat} onClose={() => setEditingCat(null)} title="编辑课程分类" organizations={organizations} initialValue={editingCat ? { name: editingCat.name, orgId: editingCat.orgId } : undefined} onSubmit={handleEditCategory} />
      <LevelModal isOpen={levelModalOpen} onClose={() => setLevelModalOpen(false)} title="新增级别分类" organizations={organizations} categories={categories} onSubmit={handleAddLevel} />
      <LevelModal isOpen={!!editingLevel} onClose={() => setEditingLevel(null)} title="编辑级别分类" organizations={organizations} categories={categories} initialValue={editingLevel ? { name: editingLevel.name, orgId: getCatName(editingLevel.catId), catId: editingLevel.catId, priceMonth: editingLevel.priceMonth, priceQuarter: editingLevel.priceQuarter, priceHalfYear: editingLevel.priceHalfYear, priceYear: editingLevel.priceYear } : undefined} onSubmit={handleEditLevel} />
      <BatchPriceModal isOpen={batchPriceModalOpen} onClose={() => setBatchPriceModalOpen(false)} selectedCount={selectedLevels.size} onSubmit={handleBatchUpdatePrices} />
      <ConfirmModal isOpen={!!deletingOrg} onClose={() => setDeletingOrg(null)} title="删除考评体系" message={`确定要删除考评体系"${deletingOrg?.name}"吗？删除后将同时删除其下的课程分类和级别分类数据。`} onConfirm={handleDeleteOrganization} />
      <ConfirmModal isOpen={batchDeletingOrgs} onClose={() => setBatchDeletingOrgs(false)} title="批量删除考评体系" message={`确定要删除选中的 ${selectedOrganizations.size} 个考评体系吗？删除后将同时删除其下的课程分类和级别分类数据。`} onConfirm={confirmBatchDeleteOrganizations} />
      <ConfirmModal isOpen={!!deletingCat} onClose={() => setDeletingCat(null)} title="删除课程分类" message={`确定要删除课程分类"${deletingCat?.name}"吗？`} onConfirm={handleDeleteCategory} />
      <ConfirmModal isOpen={!!deletingLevel} onClose={() => setDeletingLevel(null)} title="删除级别分类" message={`确定要删除级别分类"${deletingLevel?.name}"吗？`} onConfirm={handleDeleteLevel} />
      <div className="flex gap-2 mb-6">
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as 'org' | 'course' | 'level')} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {activeTab === 'org' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">考评体系列表</h3>
              <div className="flex items-center gap-2">
                {selectedOrganizations.size > 0 && (
                  <button onClick={handleBatchDeleteOrganizations} className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                    <Trash2 className="w-4 h-4" />批量删除 ({selectedOrganizations.size})
                  </button>
                )}
                <button onClick={() => setOrgModalOpen(true)} className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" />新增考评体系</button>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-center text-base font-bold text-gray-900 w-12"><input type="checkbox" checked={selectedOrganizations.size === organizations.length && organizations.length > 0} onChange={toggleSelectAllOrganizations} className="rounded border-gray-300" /></th><th className="px-4 py-3 text-left text-base font-bold text-gray-900">排序</th><th className="px-4 py-3 text-left text-base font-bold text-gray-900">ID</th><th className="px-4 py-3 text-left text-base font-bold text-gray-900">名称</th><th className="px-4 py-3 text-center text-base font-bold text-gray-900">操作</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {organizations.map((org, index) => (
                  <tr key={org.id} className={`${getOrgColor(org.id, organizations)} hover:brightness-95`}>
                    <td className="px-4 py-3 text-center bg-white"><input type="checkbox" checked={selectedOrganizations.has(org.id)} onChange={() => toggleSelectOrganization(org.id)} className="rounded border-gray-300" /></td>
                    <td className="px-4 py-3 text-sm text-gray-700">{org.sortOrder}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{org.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{org.name}</td>
                    <td className="px-4 py-3 text-center bg-white">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleMoveOrganization(org.id, 'up')} disabled={index === 0} className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                        <button onClick={() => handleMoveOrganization(org.id, 'down')} disabled={index === organizations.length - 1} className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                        <button onClick={() => setEditingOrg(org)} className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => setDeletingOrg(org)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === 'course' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">课程分类列表</h3>
              <div className="flex items-center gap-2">
                {selectedCategories.size > 0 && (
                  <button onClick={handleBatchDeleteCategories} className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                    <Trash2 className="w-4 h-4" />批量删除 ({selectedCategories.size})
                  </button>
                )}
                <button onClick={() => setCatModalOpen(true)} className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" />新增课程分类</button>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-center text-base font-bold text-gray-900 w-12"><input type="checkbox" checked={selectedCategories.size === categories.length && categories.length > 0} onChange={toggleSelectAllCategories} className="rounded border-gray-300" /></th><th className="px-4 py-3 text-left text-base font-bold text-gray-900">所属考评体系</th><th className="px-4 py-3 text-left text-base font-bold text-gray-900">所属课程分类</th><th className="px-4 py-3 text-left text-base font-bold text-gray-900">ID</th><th className="px-4 py-3 text-center text-base font-bold text-gray-900">操作</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(categories || [])
                  .map(cat => {
                    const org = (organizations || []).find(o => o.id === cat.orgId);
                    return { ...cat, orgName: org?.name || '-', orgSort: org?.sortOrder || 0 };
                  })
                  .sort((a, b) => {
                    if (a.orgSort !== b.orgSort) return a.orgSort - b.orgSort;
                    return a.name.localeCompare(b.name, 'zh-CN');
                  })
                  .map((cat) => (
                    <tr key={cat.id} className={`${getOrgColor(cat.orgId, organizations)} hover:brightness-95`}>
                      <td className="px-4 py-3 text-center bg-white"><input type="checkbox" checked={selectedCategories.has(cat.id)} onChange={() => toggleSelectCategory(cat.id)} className="rounded border-gray-300" /></td>
                      <td className="px-4 py-3 text-sm text-gray-700">{cat.orgName}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{cat.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{cat.id}</td>
                      <td className="px-4 py-3 text-center bg-white">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setEditingCat(cat)} className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => setDeletingCat(cat)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === 'level' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">级别分类列表</h3>
              <div className="flex items-center gap-2">
                {selectedLevels.size > 0 && (
                  <>
                    <button onClick={() => setBatchPriceModalOpen(true)} className="flex items-center gap-1 px-3 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700">
                      <Tag className="w-4 h-4" />批量定价 ({selectedLevels.size})
                    </button>
                    <button onClick={handleBatchDeleteLevels} className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                      <Trash2 className="w-4 h-4" />批量删除 ({selectedLevels.size})
                    </button>
                  </>
                )}
                <button onClick={() => setLevelModalOpen(true)} className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" />新增级别分类</button>
              </div>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900 w-12"><input type="checkbox" checked={selectedLevels.size === levels.length && levels.length > 0} onChange={toggleSelectAllLevels} className="rounded border-gray-300" /></th>
                  <th className="px-4 py-3 text-left text-base font-bold text-gray-900">所属考评体系</th>
                  <th className="px-4 py-3 text-left text-base font-bold text-gray-900">所属课程分类</th>
                  <th className="px-4 py-3 text-left text-base font-bold text-gray-900">名称</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900">月包</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900">季包</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900">半年包</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900">年包</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900" colSpan={4}>题型配置</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900">刷题包券码</th>
                  <th className="px-4 py-3 text-center text-base font-bold text-gray-900">操作</th>
                </tr>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                  <th className="px-2 py-2 text-center text-sm font-medium text-gray-700">单选</th>
                  <th className="px-2 py-2 text-center text-sm font-medium text-gray-700">多选</th>
                  <th className="px-2 py-2 text-center text-sm font-medium text-gray-700">判断</th>
                  <th className="px-2 py-2 text-center text-sm font-medium text-gray-700">非客观</th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(levels || [])
                  .map(lvl => {
                    const cat = (categories || []).find(c => c.id === lvl.catId);
                    const org = (organizations || []).find(o => o.id === cat?.orgId);
                    return { ...lvl, catName: cat?.name || '-', orgName: org?.name || '-', orgSort: org?.sortOrder || 0, catSort: cat?.sortOrder || 0, orgId: cat?.orgId || '' };
                  })
                  .sort((a, b) => {
                    if (a.orgSort !== b.orgSort) return a.orgSort - b.orgSort;
                    if (a.catSort !== b.catSort) return a.catSort - b.catSort;
                    const levelOrder: Record<string, number> = { '一级': 1, '二级': 2, '三级': 3, '四级': 4, '五级': 5, '六级': 6, '七级': 7, '八级': 8, '九级': 9, '十级': 10 };
                    return (levelOrder[a.name] || 99) - (levelOrder[b.name] || 99);
                  })
                  .map((lvl) => (
                    <tr key={lvl.id} className={`${getOrgColor(lvl.orgId, organizations)} hover:brightness-95`}>
                      <td className="px-4 py-3 text-center bg-white"><input type="checkbox" checked={selectedLevels.has(lvl.id)} onChange={() => toggleSelectLevel(lvl.id)} className="rounded border-gray-300" /></td>
                      <td className="px-4 py-3 text-sm text-gray-700">{lvl.orgName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{lvl.catName}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{lvl.name}</td>
                      <td className="px-4 py-3 text-center text-sm"><EditablePriceCell value={lvl.priceMonth || 0} onSave={(v) => handleUpdateLevelPrice(lvl.id, 'priceMonth', v)} /></td>
                      <td className="px-4 py-3 text-center text-sm"><EditablePriceCell value={lvl.priceQuarter || 0} onSave={(v) => handleUpdateLevelPrice(lvl.id, 'priceQuarter', v)} /></td>
                      <td className="px-4 py-3 text-center text-sm"><EditablePriceCell value={lvl.priceHalfYear || 0} onSave={(v) => handleUpdateLevelPrice(lvl.id, 'priceHalfYear', v)} /></td>
                      <td className="px-4 py-3 text-center text-sm"><EditablePriceCell value={lvl.priceYear || 0} onSave={(v) => handleUpdateLevelPrice(lvl.id, 'priceYear', v)} /></td>
                      <td className="px-2 py-3 text-center text-sm"><EditableCountCell value={lvl.singleChoiceCount || 0} onSave={(v) => handleUpdateLevelQuestionConfig(lvl.id, 'singleChoiceCount', v)} /></td>
                      <td className="px-2 py-3 text-center text-sm"><EditableCountCell value={lvl.multiChoiceCount || 0} onSave={(v) => handleUpdateLevelQuestionConfig(lvl.id, 'multiChoiceCount', v)} /></td>
                      <td className="px-2 py-3 text-center text-sm"><EditableCountCell value={lvl.judgeCount || 0} onSave={(v) => handleUpdateLevelQuestionConfig(lvl.id, 'judgeCount', v)} /></td>
                      <td className="px-2 py-3 text-center text-sm"><EditableCountCell value={lvl.programmingCount || 0} onSave={(v) => handleUpdateLevelQuestionConfig(lvl.id, 'programmingCount', v)} /></td>
                      <td className="px-4 py-3 text-center bg-white">
                        <button
                          onClick={() => { setSelectedLevelForCoupon(lvl); setCouponModalOpen(true); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors mx-auto"
                        >
                          <Eye className="w-4 h-4" />查看
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center bg-white">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setEditingLevel(lvl)} className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => setDeletingLevel(lvl)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CouponCodeModal
        isOpen={couponModalOpen}
        onClose={() => { setCouponModalOpen(false); setSelectedLevelForCoupon(null); }}
        orgName={selectedLevelForCoupon ? (() => {
          const cat = categories.find(c => c.id === selectedLevelForCoupon.catId);
          const org = cat ? organizations.find(o => o.id === cat.orgId) : null;
          return org?.name || '';
        })() : ''}
        catName={selectedLevelForCoupon ? getCatName(selectedLevelForCoupon.catId) : ''}
        levelName={selectedLevelForCoupon?.name || ''}
        levelId={selectedLevelForCoupon ? parseInt(selectedLevelForCoupon.id) : 0}
        orgId={selectedLevelForCoupon ? (() => {
          const cat = categories.find(c => c.id === selectedLevelForCoupon.catId);
          return cat?.orgId ? parseInt(cat.orgId) : 0;
        })() : 0}
        catId={selectedLevelForCoupon ? parseInt(selectedLevelForCoupon.catId) : 0}
      />
    </div>
  );
}
